import type { RequestFn } from '../http';

export type PurchaseOrderStatus =
  | 'DRAFT'
  | 'ORDERED'
  | 'CONFIRMED'
  | 'PARTIALLY_RECEIVED'
  | 'RECEIVED'
  | 'CANCELLED';

export interface PurchaseOrderItem {
  id: string;
  sourceUrl: string | null;
  articleNumber: string | null;
  name: string;
  quantity: number;
  unitPrice: number | null;
  currency: string;
  articleId: string | null;
  hasScreenshot: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseOrderDetail {
  id: string;
  orderNumber: string;
  status: PurchaseOrderStatus;
  supplierName: string | null;
  supplierId: string | null;
  supplier: { id: string; name: string } | null;
  externalOrderNo: string | null;
  orderDate: string | null;
  expectedDeliveryDate: string | null;
  targetWarehouseId: string | null;
  targetWarehouse: { id: string; name: string } | null;
  customerId: string | null;
  customer: { id: string; name: string; customerNumber: string | null } | null;
  notes: string | null;
  createdBy: { id: string; displayName: string };
  createdAt: string;
  updatedAt: string;
  items: PurchaseOrderItem[];
  delivery: { orderedQty: number; receivedQty: number };
  goodsReceipts: PurchaseOrderGoodsReceipt[];
}

export interface PurchaseOrderGoodsReceipt {
  id: string;
  status: PurchaseOrderStatus | string;
  warehouseName: string | null;
  deliveryNoteNo: string | null;
  totalQuantity: number;
  postedAt: string | null;
  createdAt: string;
}

export interface PurchaseOrderListItem {
  id: string;
  orderNumber: string;
  status: PurchaseOrderStatus;
  supplierName: string | null;
  customerName: string | null;
  warehouseName: string | null;
  itemCount: number;
  totalQuantity: number;
  orderDate: string | null;
  expectedDeliveryDate: string | null;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseOrderHeaderInput {
  supplierName?: string | null;
  supplierId?: string | null;
  externalOrderNo?: string | null;
  orderDate?: string | null;
  expectedDeliveryDate?: string | null;
  targetWarehouseId?: string | null;
  customerId?: string | null;
  notes?: string | null;
}

export interface UpdatePurchaseOrderInput extends PurchaseOrderHeaderInput {
  status?: PurchaseOrderStatus;
}

export interface PurchaseOrderItemInput {
  sourceUrl?: string | null;
  articleNumber?: string | null;
  name: string;
  quantity: number;
  unitPrice?: number | null;
  currency?: string;
  articleId?: string | null;
  screenshotRef?: string | null;
  notes?: string | null;
  /** Neu-anlegen-Fallback: legt einen Artikel im Stamm an, wenn `articleId` leer ist. */
  newArticle?: {
    manufacturer?: string | null;
    salesTitle?: string | null;
    salesDescription?: string | null;
  } | null;
}

export interface ScreenshotExtractionResult {
  extraction: {
    articleNumber: string | null;
    name: string | null;
    unitPrice: number | null;
    currency: string | null;
  };
  screenshotRef: string;
  extractionError?: string;
}

export function createPurchaseOrdersApi(request: RequestFn) {
  return {
    async listPurchaseOrders(filters?: {
      status?: PurchaseOrderStatus;
      limit?: number;
      offset?: number;
    }) {
      const qs = new URLSearchParams();
      if (filters?.status) qs.set('status', filters.status);
      if (filters?.limit !== undefined) qs.set('limit', String(filters.limit));
      if (filters?.offset !== undefined) qs.set('offset', String(filters.offset));
      const query = qs.toString();
      return request<{ orders: PurchaseOrderListItem[] }>(
        `/purchase-orders${query ? `?${query}` : ''}`
      );
    },

    async createPurchaseOrder(data: PurchaseOrderHeaderInput) {
      return request<{ order: PurchaseOrderDetail }>('/purchase-orders', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async getPurchaseOrder(id: string) {
      return request<{ order: PurchaseOrderDetail }>(`/purchase-orders/${id}`);
    },

    async updatePurchaseOrder(id: string, patch: UpdatePurchaseOrderInput) {
      return request<{ order: PurchaseOrderDetail }>(`/purchase-orders/${id}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
    },

    async cancelPurchaseOrder(id: string) {
      return request<{ success: boolean }>(`/purchase-orders/${id}`, { method: 'DELETE' });
    },

    async addPurchaseOrderItem(orderId: string, item: PurchaseOrderItemInput) {
      return request<{ item: PurchaseOrderItem }>(`/purchase-orders/${orderId}/items`, {
        method: 'POST',
        body: JSON.stringify(item),
      });
    },

    async updatePurchaseOrderItem(
      orderId: string,
      itemId: string,
      patch: Partial<PurchaseOrderItemInput>
    ) {
      return request<{ item: PurchaseOrderItem }>(
        `/purchase-orders/${orderId}/items/${itemId}`,
        { method: 'PUT', body: JSON.stringify(patch) }
      );
    },

    async removePurchaseOrderItem(orderId: string, itemId: string) {
      return request<{ success: boolean }>(
        `/purchase-orders/${orderId}/items/${itemId}`,
        { method: 'DELETE' }
      );
    },

    /** KIAN: Screenshot hochladen → Artikelnr/Bezeichnung/Preis extrahieren. */
    async extractPurchaseOrderScreenshot(file: File) {
      const formData = new FormData();
      formData.append('file', file);
      return request<ScreenshotExtractionResult>('/purchase-orders/extract-screenshot', {
        method: 'POST',
        body: formData,
      });
    },
  };
}
